import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Title,
    Legend,
    ChartOptions,
    Plugin,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Title, Legend);

export type FeatureData = {
    feature_name: string;
    friendly_name: string;
    feature_weight: number;
};

type Props = {
    data: FeatureData[];
};

// 🔧 Plugin to draw arrows and labels under x-axis
const xAxisAnnotationPlugin: Plugin<'bar'> = {
    id: 'xAxisArrows',
    afterDraw: (chart) => {
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const yAxis = chart.scales.y;

        const centerX = (xAxis.left + xAxis.right) / 2;
        const y = xAxis.bottom + 30;

        ctx.save();

        // Left arrow (lower risk)
        ctx.fillStyle = '#007bff';
        ctx.textAlign = 'right';
        ctx.font = '14px sans-serif';
        ctx.fillText('← Decreases Risk', centerX - 10, y);

        // Right arrow (higher risk)
        ctx.fillStyle = '#dc3545';
        ctx.textAlign = 'left';
        ctx.fillText('Increases Risk →', centerX + 10, y);

        ctx.restore();
    },
};

const FeatureImportanceChart: React.FC<Props> = ({ data }) => {

    const sortedData = data ? [...data].sort(
        (a, b) => Math.abs(b.feature_weight) - Math.abs(a.feature_weight)
    ) : [];

    const chartData = {
        labels: sortedData.map((item) => {
            return item.friendly_name;
        }),
        datasets: [
            {
                label: 'Risk Contribution',
                data: sortedData.map((item) => item.feature_weight),
                backgroundColor: sortedData.map((item) =>
                    item.feature_weight >= 0 ? 'rgba(220, 53, 69, 0.7)' : 'rgba(0, 123, 255, 0.7)'
                ),
                borderColor: sortedData.map((item) =>
                    item.feature_weight >= 0 ? 'rgba(220, 53, 69, 1)' : 'rgba(0, 123, 255, 1)'
                ),
                borderWidth: 1,
            },
        ],
    };

    const options: ChartOptions<'bar'> = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: {
                display: true,
                text: 'How Each Factor Affects Risk',
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.raw as number;
                        const direction = value >= 0 ? 'Increases Risk' : 'Decreases Risk';
                        return `${context.label}: ${Math.abs(value).toFixed(2)} (${direction})`;
                    },
                },
            },
        },
        layout: {
            padding: {
                bottom: 40, // room for the arrows
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Effect on Risk Score',
                },
                beginAtZero: true,
            },
        },
    };

    return (
        <div style={{ height: '400px' }}>
            <Bar data={chartData} options={options} plugins={[xAxisAnnotationPlugin]} />
        </div>
    );
};

export default FeatureImportanceChart;
